import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, catchError } from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { Router } from '@angular/router';
import { APP_ROUTES } from '../constants/routes.constants';

/**
 * Authentication HTTP Interceptor
 *
 * UBICACIÓN CORRECTA: /core/interceptors/
 * RAZÓN: Los interceptors HTTP son infraestructura transversal que afecta
 * TODA la aplicación, no son específicos del dominio de autenticación.
 *
 * Responsibilities:
 * - Attach authentication tokens to outgoing HTTP requests
 * - Handle global authentication errors (401, 403)
 * - Skip auth for public endpoints
 * - Redirect users based on auth state
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip authentication for specific endpoints
  if (shouldSkipAuth(req.url)) {
    return next(req);
  }

  // Get current session
  const session = authService.getCurrentSession();

  if (!session?.tokens?.accessToken) {
    // No token available, proceed without auth header
    return next(req);
  }

  // Clone request and add auth header
  const authReq = addAuthHeader(req, session.tokens.accessToken);

  return next(authReq).pipe(
    catchError(error => {
      // Handle authentication errors
      // if (error.status === 401) {
      //   return handleUnauthorized(authService, router, req, next);
      // }

      // if (error.status === 403) {
      //   return handleForbidden(authService, router);
      // }

      // Re-throw other errors
      return throwError(() => error);
    })
  );
};

/**
 * Determine if authentication should be skipped for this request
 */
function shouldSkipAuth(url: string): boolean {
  const skipPatterns = [
    APP_ROUTES.AUTH.BASE,     // '/auth/'
    '/public/',
    '/assets/',
    'oauth',
    'login'
  ];

  return skipPatterns.some(pattern => url.includes(pattern));
}

/**
 * Add authorization header to request
 */
function addAuthHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      'Authorization': `Bearer ${token}`,
    }
  });
}

/**
 * Handle 401 Unauthorized errors
 */
function handleUnauthorized(
  authService: AuthService,
  router: Router,
  originalReq: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  // For MVP, just logout and redirect to login
  authService.logout();
  router.navigate([APP_ROUTES.AUTH.LOGIN]);
  return throwError(() => new Error('Authentication failed'));
}

/**
 * Handle 403 Forbidden errors
 */
function handleForbidden(
  authService: AuthService,
  router: Router
): Observable<never> {
  // User is authenticated but doesn't have permission
  console.warn('Access forbidden - insufficient permissions');
  router.navigate([APP_ROUTES.UNAUTHORIZED]);
  return throwError(() => new Error('Access forbidden'));
}
